import { useMemo, useState } from "react";
import Slider from "react-slick";
import { MdPhotoLibrary } from "react-icons/md";
import GalleryModal from "./GalleryModal";
import { buildPackageGalleryData, NO_IMAGE_URL } from "./utils/packageGallery";
import appendLoopingSlickDots from "@/Common/LoopingSlickDots";
import styles from "./HolidayDetail.module.css";


function handleImageError(event) {
  if (event.currentTarget.src === NO_IMAGE_URL) return;
  event.currentTarget.src = NO_IMAGE_URL;
}

function GalleryTile({ image, badge, onClick, className = "", showIcon = false }) {
  return (
    <button type="button" className={`${styles.tile} ${className}`} onClick={onClick}>
      <img src={image || NO_IMAGE_URL} alt="" onError={handleImageError} />
      <span className={styles.badge}>
        {showIcon ? <MdPhotoLibrary aria-hidden="true" /> : null}
        {badge}
      </span>
    </button>
  );
}

export default function PackageGallery({
  packageName,
  packageImages = [],
  activityImages = [],
  groupedHotels = [],
  reviewImages = [],
  banner = "",
  thumb = "",
}) {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [modalCategory, setModalCategory] = useState("all");
  const [modalImageSrc, setModalImageSrc] = useState("");

  const galleryData = useMemo(
    () =>
      buildPackageGalleryData({
        packageImages,
        activityImages,
        groupedHotels,
        reviewImages,
        banner,
        thumb,
      }),
    [packageImages, activityImages, groupedHotels, reviewImages, banner, thumb]
  );

  const { categories, counts, previewTiles } = galleryData;

  const openGallery = (category, imageSrc = "") => {
    setModalCategory(category);
    setModalImageSrc(imageSrc);
    setGalleryOpen(true);
  };

  const sliderItems = [
    {
      key: "property",
      category: "property",
      image: previewTiles.property,
      badge: `Property (${counts.property})`,
    },
    {
      key: "clientReview",
      category: "clientReview",
      image: previewTiles.clientReview,
      badge: `Client Reviews (${counts.clientReview})`,
    },
    {
      key: "sightseeing",
      category: "activity",
      image: previewTiles.sightseeing,
      badge: `Sightseeing (${counts.activity})`,
    },
    {
      key: "activities",
      category: "activity",
      image: previewTiles.activities,
      badge: `Activities (${counts.activity})`,
    },
    {
      key: "all",
      category: "all",
      image: previewTiles.main,
      badge: `View All (${counts.all})`,
      showIcon: true,
    },
  ];

  const sliderSettings = {
    dots: sliderItems.length > 2,
    infinite: sliderItems.length > 2,
    speed: 450,
    slidesToShow: Math.min(2, sliderItems.length),
    slidesToScroll: 1,
    arrows: sliderItems.length > 2,
    // prevArrow: sliderItems.length > 2 ? <GalleryNavArrow direction="prev" /> : null,
    // nextArrow: sliderItems.length > 2 ? <GalleryNavArrow direction="next" /> : null,
    dotsClass: `slick-dots ${styles.carouselDots}`,
    appendDots: appendLoopingSlickDots(`slick-dots ${styles.carouselDots}`),
  };

  return (
    <>
      <div className={styles.galleryDesktop}>
        <GalleryTile
          className={styles.sideTile}
          image={previewTiles.sightseeing}
          badge={`Sightseeing (${counts.activity})`}
          onClick={() => openGallery("activity", previewTiles.sightseeing)}
        />
        <GalleryTile
          className={styles.mainTile}
          image={previewTiles.main}
          badge={`View All (${counts.all})`}
          showIcon
          onClick={() => openGallery("all", previewTiles.main)}
        />
        <GalleryTile
          className={styles.sideTile}
          image={previewTiles.property}
          badge={`Property (${counts.property})`}
          onClick={() => openGallery("property", previewTiles.property)}
        />
        <GalleryTile
          className={styles.sideTile}
          image={previewTiles.clientReview}
          badge={`Client Review (${counts.clientReview})`}
          onClick={() => openGallery("clientReview", previewTiles.clientReview)}
        />
        <GalleryTile
          className={styles.sideTile}
          image={previewTiles.activities}
          badge={`Activities (${counts.activity})`}
          onClick={() => openGallery("activity", previewTiles.activities)}
        />
      </div>

      <div className={styles.galleryMobile}>
        <div className={styles.gallerySliderWrap}>
          <Slider {...sliderSettings}>
            {sliderItems.map((item) => (
              <div key={item.key} className={styles.gallerySliderSlide}>
                <GalleryTile
                  image={item.image}
                  badge={item.badge}
                  showIcon={item.showIcon}
                  onClick={() => openGallery(item.category, item.image)}
                />
              </div>
            ))}
          </Slider>
        </div>
      </div>

      <GalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        title={packageName}
        categories={categories}
        initialCategory={modalCategory}
        initialImageSrc={modalImageSrc}
      />
    </>
  );
}
